import React from "react";

import { withFirebaseAuth } from "js/react/views/Auth/FirebaseAuthContext";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { Authenticate } from "js/react/views/Auth/Authenticate";
import ErrorMessage from "js/app/ErrorMessage";
import { sanitizeJavascriptInjection } from "js/core/utilities/utilities";

/**
 * Fetches `serverContext` for player via a special API.
 * This is done to facilitate client-side auth.
 */
class PlayerServerContext extends React.Component {
    state = { status: "loading" };

    async componentDidMount() {
        this.load();
    }

    load = async () => {
        try {
            const user = this.props.firebaseUser;
            let fallback = new URL(window.location).searchParams.get("fallback") || "false";
            fallback = sanitizeJavascriptInjection(fallback, "fallback", "false");
            const res = await fetch("/player-context/" + window.serverContext.contextId + "?fallback=" + fallback, {
                headers: {
                    "Accept": "application/json",
                    ...(user
                        ? { "Authorization": `Bearer ${await user.getIdToken()}` }
                        : {}
                    )
                },
            });
            if (!res.ok) {
                // grab the old server-side error page
                const html = await res.text();

                // we have to check for 401 as well because it can be thrown by the
                // PresentationController.getForPermission() function when user is required
                // to confirm their identity
                if (!this.props.firebaseUser && (res.status === 403 || res.status === 401)) {
                    // if the presentation is forbidden, prompt the user to log in
                    const unsubscribe = this.props.auth.onAuthStateChanged(user => {
                        if (user != null) {
                            unsubscribe();
                            this.load();
                        }
                    });
                    this.setState({ status: "auth" });
                } else {
                    this.setState({ status: "errorHtml", errorHtml: html });
                }

                return;
            }
            const data = await res.json();
            // because the global window.serverContext has already been used exported via
            // an ES6 module, we have to keep the object instance and overwrite its values
            for (const [key, value] of Object.entries(data.options.serverContext)) {
                window.serverContext[key] = value;
            }

            // add the presentation slug to private URLs, as we'd be leaking private information if the server did it
            const { linkId, presentationId, slug, name, isPublic } = window.serverContext;
            if (!isPublic) {
                history.replaceState({}, `Beautiful.ai - ${name}`, `/player/${linkId || presentationId}/${slug}`);
            }

            this.setState({ status: "loaded" });
        } catch (err) {
            console.error(err);
            this.setState({ status: "errorText", errorText: err.message });
        }
    }

    render() {
        switch (this.state.status) {
            case "loading":
                // TODO: we could add a spinner here, but it's already fast
                return <div></div>;
            case "loaded":
                return this.props.children;
            case "errorHtml":
                return <div dangerouslySetInnerHTML={{ __html: this.state.errorHtml }} />;
            case "errorText":
                return <ErrorMessage message={this.state.errorText} />;
            case "auth":
                return <Authenticate defaultPage="signIn" />;
        }
    }
}

export default withFirebaseAuth(withFirebaseUser(PlayerServerContext));
